import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowRight } from "lucide-react";
import { useLang } from "@/lib/lang";
import { introDelay } from "@/lib/lenis";
import { EASE, Eyebrow, MaskLines } from "@/components/motion";
import { HeroEmblem } from "@/components/HeroEmblem";
import { NavAnchor } from "@/components/Nav";
import { AppleIcon, PlayIcon } from "@/components/StoreBadges";

const fadeUp = (delay) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 1, ease: EASE, delay } });

export const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const [delay] = useState(introDelay);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const yArt = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden pb-14 pt-28 sm:pt-36">
      <div className="hero-grid pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute -right-[20%] -top-[30%] h-[80vw] w-[80vw] rounded-full bg-[radial-gradient(circle,rgba(34,211,238,0.16),rgba(47,107,255,0.08)_40%,transparent_65%)]" />

      <div className="container-x relative grid items-center gap-y-12 lg:min-h-[calc(100svh-13rem)] lg:grid-cols-12">
        <motion.div style={{ y: yText, opacity: fade }} className="relative z-20 lg:col-span-8">
          <motion.div {...fadeUp(delay)}>
            <Eyebrow testid="hero-eyebrow">{t.hero.eyebrow}</Eyebrow>
          </motion.div>
          <h1 key={lang} data-testid="hero-headline" className="hero-title mt-7 font-display font-extrabold">
            <MaskLines lines={t.hero.lines} delay={delay + 0.1} lineClassName={(i) => (i === 2 ? "text-z-gradient" : "")} />
          </h1>
          <motion.p {...fadeUp(delay + 0.55)} data-testid="hero-subtitle" className="mt-8 max-w-xl text-base leading-relaxed text-white/65 sm:text-lg">
            {t.hero.sub}
          </motion.p>
          <motion.div {...fadeUp(delay + 0.7)} className="mt-10 flex flex-wrap items-center gap-3">
            <NavAnchor id="apps" testid="hero-explore-button" className="btn-primary group">
              {t.hero.ctaPrimary}
              <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
            </NavAnchor>
            <NavAnchor id="contact" testid="hero-contact-button" className="btn-ghost">{t.hero.ctaSecondary}</NavAnchor>
          </motion.div>
          <motion.div {...fadeUp(delay + 0.85)} data-testid="hero-store-note" className="mt-8 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white/45">
            <PlayIcon className="h-4 w-4" />
            <AppleIcon className="h-4 w-4 text-white/70" />
            {t.hero.stores}
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yArt }} className="relative z-10 lg:col-span-4">
          <HeroEmblem delay={delay + 0.2} />
        </motion.div>
      </div>

      <motion.div {...fadeUp(delay + 1)} className="container-x relative mt-16 grid grid-cols-2 gap-6 border-t border-white/10 pt-6 sm:grid-cols-4">
        {t.hero.stats.map(([k, v], i) => (
          <div key={i} data-testid={`hero-stat-${i}`}>
            <p className="font-display text-xl font-bold tracking-tight sm:text-2xl">{k}</p>
            <p className="mt-1 text-xs text-white/45 sm:text-sm">{v}</p>
          </div>
        ))}
        <NavAnchor id="apps" testid="hero-scroll-cue" className="group col-span-2 flex items-center justify-end gap-3 self-center font-mono text-[11px] uppercase tracking-[0.3em] text-white/50 transition-colors hover:text-white sm:col-span-1">
          {t.hero.scroll}
          <span className="grid h-10 w-10 place-items-center rounded-full border border-white/15 transition-transform duration-500 group-hover:rotate-45">
            <ArrowDownRight className="h-4 w-4" />
          </span>
        </NavAnchor>
      </motion.div>
    </section>
  );
};
